// src/pages/orderDelivery/OrderHistory.jsx
import { useState, useEffect } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../services/firebase";
import { useBuyer } from "../../contexts/BuyerContext";
import Header from "../../components/Header";
import { ChevronDown, ChevronUp, CheckCircle, Calendar, Award } from "lucide-react";
import "./OrderHistory.css";

// Safe numeric parser helper
const parseNum = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
};

// Safe toFixed helper that NEVER crashes regardless of data type
const safeToFixed = (val, decimals = 3) => {
  const n = parseNum(val);
  return n.toFixed(decimals);
};

// Safe rupee formatter
const formatRupee = (amount) => {
  const n = parseNum(amount);
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Safe date-time formatter
const formatDateTime = (dateValue) => {
  if (!dateValue) return "N/A";
  let date;
  if (typeof dateValue === "object" && dateValue.seconds) {
    date = new Date(dateValue.seconds * 1000);
  } else {
    date = new Date(dateValue);
  }
  if (isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const OrderHistory = () => {
  const { buyer } = useBuyer();
  const [settlementHistory, setSettlementHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      const buyerId = buyer?.buyerId;
      if (!buyerId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const historyQ = query(
          collection(db, "myorders"),
          where("buyerId", "==", buyerId)
        );
        const historySnap = await getDocs(historyQ);
        const historyData = historySnap.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => {
            const timeA = a.settlementTimestamp || a.createdAt || "";
            const timeB = b.settlementTimestamp || b.createdAt || "";
            return timeB.localeCompare(timeA);
          });

        setSettlementHistory(historyData);
      } catch (err) {
        console.error("[OrderHistory] Error fetching settlement history:", err);
        setError(`Failed to load order history: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [buyer?.buyerId]);

  return (
    <>
      <Header title="Order History" />
      <div className="oh-page">
        <div className="oh-title-section">
          <h1 className="oh-title">Settlement & Order History</h1>
        </div>

        {loading && <div className="loading-state">Loading order history...</div>}
        {error && <div className="error-state">{error}</div>}

        {!loading && !error && (
          <div>
            {settlementHistory.length === 0 ? (
              <div className="empty-state" style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
                No settled orders found in your history.
              </div>
            ) : (
              settlementHistory.map((settlement) => {
                const isExpanded = expandedHistory === settlement.id;
                const { groupSummary, paymentDetails, orders: stOrders } = settlement;
               const totalItems = paymentDetails?.paidQtySum ?? groupSummary?.totalItems ?? 0;
                const totalFineWt = parseNum(paymentDetails?.totalFineWtDelivered ?? groupSummary?.displayFineWeight);
                const totalPaid = parseNum(paymentDetails?.totalAmountPaid);
                const settlementDate = formatDateTime(settlement.settlementTimestamp || settlement.createdAt);

                return (
                  <div key={settlement.id} className="oh-card">
                    {/* Header */}
                    <div
                      className="oh-card-header"
                      onClick={() => setExpandedHistory(isExpanded ? null : settlement.id)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="oh-seller-info">

                        <div className="oh-timestamp">
                          <Calendar size={13} style={{ display: "inline", marginRight: "4px" }} />
                          Settled {settlementDate} • {totalItems} item{totalItems !== 1 ? "s" : ""}
                        </div>
                      </div>

                      <div className="oh-header-right">
                        <div className="oh-summary-pill">
                          <span className="oh-fine-wt">Fine Wt: {safeToFixed(totalFineWt, 3)}g</span>
                          <span className="oh-amount-paid">Paid: {formatRupee(totalPaid)}</span>
                        </div>
                        <button className="oh-toggle-btn" aria-label="Toggle details">
                          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                      </div>
                    </div>

                    {/* Body */}
                    {isExpanded && (
                      <div className="oh-card-body">
                        {/* Settlement Metadata Box */}
                        <div className="oh-meta-box">
                          <div className="oh-meta-row">
                            <div className="oh-meta-item">
                              <span className="oh-meta-label">Settlement ID</span>
                              <span className="oh-settlement-id">{settlement.settlementId || settlement.id}</span>
                            </div>
                            <div className="oh-meta-item">
                              <span className="oh-meta-label">Total Fine Wt</span>
                              <span className="oh-meta-value">{safeToFixed(totalFineWt, 3)}g</span>
                            </div>
                            <div className="oh-meta-item">
                              <span className="oh-meta-label">Total Amount Paid</span>
                              <span className="oh-meta-value">{formatRupee(totalPaid)}</span>
                            </div>
                            <div className="oh-meta-item">
                              <span className="oh-meta-label">Items Count</span>
                              <span className="oh-meta-value">{totalItems}</span>
                            </div>
                          </div>

                          {/* RTGS Bucket Breakdown */}
                          {paymentDetails?.rtgsBucket && (
                            <div className="oh-bucket-section">
                              <div className="oh-bucket-title">
                                RTGS Bucket (Fine Wt: {safeToFixed(paymentDetails.rtgsBucket.calculatedFineWt, 3)}g)
                              </div>

                              {/* Metal Entries */}
                              {paymentDetails.rtgsBucket.metalEntries && paymentDetails.rtgsBucket.metalEntries.length > 0 && (
                                <table className="oh-metal-table">
                                  <thead>
                                    <tr>
                                      <th>Gross Wt</th>
                                      <th>Purity</th>
                                      <th>Fine Wt</th>
                                      <th>Bill No</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paymentDetails.rtgsBucket.metalEntries.map((m, idx) => (
                                      <tr key={idx}>
                                        <td>{safeToFixed(m.grossWt, 3)}g</td>
                                        <td>{m.purity}%</td>
                                        <td>{safeToFixed(m.fineWt, 3)}g</td>
                                        <td>{m.billNo || "N/A"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}

                              {/* Payment details */}
                              <div style={{ marginTop: "8px", fontSize: "0.825rem" }}>
                                {paymentDetails.rtgsBucket.paymentMethod === "physical" ? (
                                  paymentDetails.rtgsBucket.physicalCash && (
                                    <div>
                                      <strong>Physical Cash:</strong> {formatRupee(paymentDetails.rtgsBucket.physicalCash.amount)}{" "}
                                      {paymentDetails.rtgsBucket.physicalCash.collectionReceiptNo &&
                                        `(Receipt #: ${paymentDetails.rtgsBucket.physicalCash.collectionReceiptNo})`}
                                    </div>
                                  )
                                ) : (
                                  paymentDetails.rtgsBucket.banking && (
                                    <div>
                                      <strong>Banking Total:</strong> {formatRupee(paymentDetails.rtgsBucket.banking.totalAmount)}
                                      {paymentDetails.rtgsBucket.banking.entries?.map((b, idx) => (
                                        <div key={idx} style={{ paddingLeft: "10px" }}>
                                          • {formatRupee(b.amount)} {b.utr ? `(UTR: ${b.utr})` : ""}
                                        </div>
                                      ))}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                          {/* Metal Bucket Breakdown */}
                          {paymentDetails?.metalBucket && (
                            <div className="oh-bucket-section">
                              <div className="oh-bucket-title">
                                Metal Bucket (Fine Wt: {safeToFixed(paymentDetails.metalBucket.calculatedFineWt, 3)}g)
                              </div>
                              {paymentDetails.metalBucket.metalEntries && paymentDetails.metalBucket.metalEntries.length > 0 && (
                                <table className="oh-metal-table">
                                  <thead>
                                    <tr>
                                      <th>Gross Wt</th>
                                      <th>Purity</th>
                                      <th>Fine Wt</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paymentDetails.metalBucket.metalEntries.map((m, idx) => (
                                      <tr key={idx}>
                                        <td>{safeToFixed(m.grossWt, 3)}g</td>
                                        <td>{m.purity}%</td>
                                        <td>{safeToFixed(m.fineWt, 3)}g</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Orders Included in Settlement */}
                        <div className="oh-orders-header">Orders ({stOrders?.length || 0})</div>
                        {stOrders && stOrders.map((order, oIdx) => {
                          const imageUrl = order.images?.[0]?.url?.trim() || order.imageUrl?.trim();
                          const grossWtVal = order.displayGrossWt ?? order.grossWt;
                          const netWtVal = order.displayNetWt ?? order.netWt;
                          const fineWtVal = order.displayFineWt ?? order.fineWeight;
                          const mcVal = order.displayMc ?? order.totalMc;
                          const qty = order.quantity || order.variantCount || 1;

                          return (
                            <div key={order.id || order.docId || oIdx} className="oh-product-card">
                              {imageUrl ? (
                                <img src={imageUrl} alt={order.productName || "Product"} className="oh-product-image" />
                              ) : (
                                <div className="oh-product-image" style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", color: "#9ca3af" }}>
                                  No Image
                                </div>
                              )}

                              <div className="oh-product-details">
                                <div className="oh-product-name">{order.productName || "Unnamed Product"}</div>

                                <div className="oh-badge-group">
                                  {order.purity && (
                                    <span className="oh-badge oh-badge-huid">
                                      <Award size={10} style={{ display: "inline", marginRight: "3px" }} />
                                      {order.purity} HUID
                                    </span>
                                  )}
                                  <span className="oh-badge oh-badge-paid">
                                    <CheckCircle size={10} style={{ display: "inline", marginRight: "3px" }} />
                                    {order.orderStatus || "buyerPaid"}
                                  </span>
                                  {order.paymentMethod && (
                                    <span className="oh-badge oh-badge-pm">{order.paymentMethod}</span>
                                  )}
                                </div>

                                <div className="oh-metrics-grid">
                                  <div>Fine Wt: <strong>{safeToFixed(fineWtVal, 3)}g</strong></div>
                                  <div>Gross Wt: <strong>{safeToFixed(grossWtVal, 3)}g</strong></div>
                                  <div>Net Wt: <strong>{safeToFixed(netWtVal, 3)}g</strong></div>
                                  <div>MC: <strong>{formatRupee(mcVal)}</strong></div>
                                  <div>Qty: <strong>{qty}</strong></div>
                                </div>

                                {/* Size Wise Details */}
                                {order.orderDetail && Object.keys(order.orderDetail).length > 0 && (
                                  <div className="oh-size-cards">
                                    {Object.entries(order.orderDetail).map(([sizeName, detail]) => (
                                      <div key={sizeName} className="oh-size-chip">
                                        <strong>{sizeName}</strong>: Qty {detail.qty} | Gross {safeToFixed(detail.grossWT, 3)}g | Net {safeToFixed(detail.netwt, 3)}g
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default OrderHistory;
